import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Loader2, MapPin } from 'lucide-react';
import SiteFooter from '../components/SiteFooter';
import ModonLogo from '../components/ModonLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Register() {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    factoryName: '', ownerName: '', email: '', phone: '', city: '',
    activity: '', address: '', password: '', lat: '', lng: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm((f) => ({ ...f, lat: String(pos.coords.latitude.toFixed(6)), lng: String(pos.coords.longitude.toFixed(6)) })),
      () => setErr('تعذر الحصول على الموقع'),
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const payload = {
        ...form,
        lat: form.lat ? parseFloat(form.lat) : null,
        lng: form.lng ? parseFloat(form.lng) : null,
      };
      await register(payload);
      nav('/factory');
    } catch (e) {
      const d = e?.response?.data?.detail;
      let msg = 'تعذّر التسجيل';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length) msg = d.map((x) => x.msg || x).join('، ');
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modon-app-shell flex flex-col" dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="modon-card w-full max-w-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <ModonLogo size={48}/>
              <div>
                <h1 className="text-xl font-extrabold text-slate-900">{t('register.title')}</h1>
                <p className="text-xs text-slate-500">{t('register.subtitle')}</p>
              </div>
            </div>
            <LanguageSwitcher/>
          </div>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <Field label={t('register.factoryName')} value={form.factoryName} onChange={set('factoryName')} required testid="reg-factory-name" />
            <Field label={t('register.ownerName')} value={form.ownerName} onChange={set('ownerName')} required testid="reg-owner-name" />
            <Field label={t('register.email')} type="email" value={form.email} onChange={set('email')} required testid="reg-email" />
            <Field label={t('register.phone')} value={form.phone} onChange={set('phone')} required testid="reg-phone" />
            <Field label={t('register.city')} value={form.city} onChange={set('city')} required testid="reg-city" />
            <Field label={t('register.activity')} value={form.activity} onChange={set('activity')} required testid="reg-activity" />
            <div className="col-span-2">
              <Field label={t('register.address')} value={form.address} onChange={set('address')} testid="reg-address" />
            </div>
            <Field label={t('register.password')} type="password" value={form.password} onChange={set('password')} required testid="reg-password" />
            <div /> {/* spacer */}
            <div className="col-span-2 grid grid-cols-2 gap-3 items-end">
              <Field label="Latitude" value={form.lat} onChange={set('lat')} testid="reg-lat" />
              <Field label="Longitude" value={form.lng} onChange={set('lng')} testid="reg-lng" />
            </div>
            <Button type="button" variant="outline" onClick={useMyLocation} className="col-span-2 text-xs" data-testid="reg-use-location-btn">
              <MapPin size={14} className="mx-1" /> GPS
            </Button>
            {err && <div className="col-span-2 text-red-600 text-sm" data-testid="reg-error">{err}</div>}
            <Button type="submit" disabled={busy} className="col-span-2 bg-[#1a4d80] hover:bg-[#143d68]" data-testid="reg-submit-btn">
              {busy ? <Loader2 className="animate-spin mx-2" size={16} /> : null} {t('register.submit')}
            </Button>
          </form>
          <div className="mt-4 text-sm text-center text-slate-600">
            {t('register.haveAccount')} <Link to="/login" className="text-[#1a4d80] font-bold">{t('register.loginLink')}</Link>
          </div>
        </Card>
      </div>
      <SiteFooter />
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-xs mb-1 block">{label}</Label>
      <Input data-testid={testid} {...props} />
    </div>
  );
}
